/**
 * The two flags that gate the PlateJS editor, as one spelling each.
 *
 * The tag name and the lookup key are deliberately the same string: a Sentry
 * search reads `plate_js_task_modal:true`, and anyone reading that query should be
 * able to grep the flag name and find where it is switched on.
 */
export const PLATE_JS_FLAGS = {
	description: 'plate_js_task_modal',
	comments: 'plate_js_task_modal_comments',
} as const;

type FeatureFlagNode = {key?: string | null} | null;

type TaggableViewer = {
	company?: {id?: string | null} | null;
	availableFeatureFlags?: readonly FeatureFlagNode[] | null;
};

export type SentryScopeTags = {
	company_id?: string;
	plate_js_task_modal: boolean;
	plate_js_task_modal_comments: boolean;
};

/**
 * The backend id inside a Relay global id, or undefined if it does not hold one.
 *
 * `atob` throws on input that is not base64, and this runs inside Sentry scope
 * configuration where a throw would cost every tag and the user context set
 * beside them — so an unreadable id yields no tag rather than an exception.
 */
const backendIdOf = (globalId: string | null | undefined, type: string): string | undefined => {
	if (!globalId) return undefined;
	try {
		const decoded = atob(globalId);
		return decoded.startsWith(`${type}:`) ? decoded.slice(type.length + 1) : undefined;
	} catch {
		return undefined;
	}
};

/**
 * The tags that make a company, and the rollout cohort it belongs to, findable in Sentry.
 *
 * Sentry indexes tags and nothing else — user context is attached to an event but is
 * not searchable — so anything we want to filter or alert on has to be a tag.
 *
 * Tagging the flag state rather than a company list means the cohort identifies
 * itself: the query is `plate_js_task_modal:true`, which stays correct as the
 * rollout widens and works the same however companies were selected. There is no
 * list to keep in step with the flag.
 *
 * The flags are read from the viewer passed in, never from the global feature-flag
 * cache. That cache is hydrated by a different query than the one behind the
 * component that configures this scope, so on a fresh browser tab it can still be
 * empty here — which would record "flag off" for a company that has it on, for the
 * rest of the session. Taking them as an argument makes the result independent of
 * which query resolved first.
 */
export const sentryScopeTags = (viewer: TaggableViewer): SentryScopeTags => {
	const flagKeys = new Set((viewer?.availableFeatureFlags ?? []).map(flag => flag?.key));
	const companyId = backendIdOf(viewer?.company?.id, 'Company');

	return {
		...(companyId ? {company_id: companyId} : {}),
		[PLATE_JS_FLAGS.description]: flagKeys.has(PLATE_JS_FLAGS.description),
		[PLATE_JS_FLAGS.comments]: flagKeys.has(PLATE_JS_FLAGS.comments),
	};
};
